import { ProductTypeEnum } from "@kanpla/api-contract";

import type { Allowance, Discount, Product } from "~/api/products/types";
import { getHybridBillingProductDiscount } from "~/utils/getHybridBillingProductDiscount";
import { shouldShowListStrikethrough } from "~/utils/hybrid-billing-display";
import { type StockStatus } from "~/utils/stock/infer-stock-status";

import { parseDisplayVariant } from "./parse-display-variant";
import { resolveDisplayUnitPrices } from "./resolve-display-totals";
import { type KioskDisplayProductList } from "./types";

type ParseDisplayProductListArgs = {
  product: Product;
  hybridBillingDiscounts: Discount[];
  allowances: Allowance[];
  isLimitOver: boolean;
  remaining: number | null;
  stockStatus: StockStatus;
  showPricesInclVat: boolean;
};

export function parseDisplayProductList({
  product,
  hybridBillingDiscounts,
  allowances,
  isLimitOver,
  remaining,
  stockStatus,
  showPricesInclVat
}: ParseDisplayProductListArgs): KioskDisplayProductList {
  const hybridBillingDiscount = getHybridBillingProductDiscount({
    product: {
      discount: product.discount,
      unitPrice: product.unitPriceInclVat
    },
    discounts: hybridBillingDiscounts,
    allowances
  });

  const productDiscounts: KioskDisplayProductList["discounts"] = [];

  let unitDisplayPrice = product.unitPriceInclVat;
  let discountPercentage = null;
  let allowance: KioskDisplayProductList["allowance"] = null;
  if (hybridBillingDiscount) {
    const isUnitPriceAllowance =
      hybridBillingDiscount.allowanceType === "unitPrice";

    // Drop an exhausted non-unitPrice discount: the basket has no other
    // isAllowanceOver gate, so the tag would still zero the line with 0 left.
    if (isUnitPriceAllowance || !hybridBillingDiscount.isAllowanceOver) {
      productDiscounts.push({
        id: hybridBillingDiscount.id,
        type: "hybrid-billing"
      });
    }

    if (shouldShowListStrikethrough(hybridBillingDiscount)) {
      unitDisplayPrice =
        product.unitPriceInclVat - hybridBillingDiscount.amount;
      discountPercentage = hybridBillingDiscount.percentage;
    }

    if (hybridBillingDiscount.allowanceType !== null) {
      // Keep the allowance even when exhausted so the modal can show a
      // "used up" hint. Badges guard on `isAllowanceOver` themselves.
      allowance =
        allowances.find(
          ({ discountId }) => discountId === hybridBillingDiscount.id
        ) ?? null;
    }
  }

  const partialBaseProduct: Omit<KioskDisplayProductList["product"], "type"> = {
    name: product.name,
    description: product.description,
    id: product.id,
    tags: product.tags ?? [],
    allergens: product.allergensDetailed,
    imageUrl: product.imageUrl,
    categoryId: product.categoryId,
    barcodeIds: product.barcodeIds,
    stamps: product.stamps,
    isLimitOver,
    stockStatus,
    disabled:
      isLimitOver ||
      stockStatus === "reserved" ||
      stockStatus === "out-of-stock",
    remaining,
    orderMin: product.orderMin
  };

  let baseProduct: KioskDisplayProductList["product"] | null = null;

  if (product.type === ProductTypeEnum.VARIANTS && product.options.length > 0) {
    baseProduct = {
      ...partialBaseProduct,
      type: "variant",
      variants: product.options.map((option) =>
        parseDisplayVariant({
          option,
          discountPercentage,
          showPricesInclVat
        })
      )
    };
  } else {
    baseProduct = {
      ...partialBaseProduct,
      type: "regular"
    };
  }

  if (product.type === ProductTypeEnum.WEIGHTED) {
    if (product.unitSystem === "piece") {
      throw new Error("Weighted products cannot have unitSystem 'piece'");
    }
    baseProduct = {
      ...partialBaseProduct,
      type: "weighted",
      unitSystem: product.unitSystem,
      containerWeight: product.containerWeight
    };
  }

  if (!baseProduct) {
    throw new Error("Base product not found");
  }

  return {
    product: baseProduct,
    discounts: productDiscounts,
    allowance,
    ...resolveDisplayUnitPrices({
      showPricesInclVat,
      unitPriceExclVat: product.unitPriceExclVat,
      unitPriceInclVat: product.unitPriceInclVat,
      discountedUnitPriceInclVat: unitDisplayPrice
    }),
    // servingBasis is a string enum at runtime, so the api shape already matches.
    nutrition: product.nutrition ?? null,
    co2Rating: product.environmental?.rating ?? null
  };
}
